// De weergavevolgorde van de losse antwoorden op één vraag.
//
// Bewust NIET aan de Firestore-query overgelaten: de twee schermen die deze
// lijst tonen (alle antwoorden, en die van één deelnemer) hebben verschillende
// filters, en niet elke filter+sorteerrichting heeft een samengestelde index.
// Een richting zonder index geeft geen lege lijst maar een gefaalde listener —
// die is stil, en het scherm ziet eruit als "geen antwoorden".
// AI-CONTEXT: See docs/pitfalls.md#database-firestore-missing-composite-index
// AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
//
// Matrixantwoorden koppelen op RowID, niet op RowTitle: een hernoemde rij houdt
// zijn ID, maar zijn titel is bevroren op het moment van antwoorden.
// AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows

/**
 * @param {string} [type] - `Type` van een QuestionnaireFields-doc of een antwoord.
 * @returns {boolean} Of het om een matrixvraag gaat.
 */
const isMatrixType = (type) => type === 'matrix-one' || type === 'matrix-multiple';

/**
 * @param {Array<object>} responses - QuestionnairesResponses van één vraag.
 * @param {Array<object>} matrixRows - MatrixQuestionRows van diezelfde vraag.
 * @param {string} [fieldType] - `Type` van het QuestionnaireFields-doc. Dit is
 *   de bron: een handmatig toegevoegd antwoord draagt geen vraagtype, en dat
 *   antwoord staat als nieuwste vooraan.
 * @returns {Array<object>} Bij een matrixvraag op rijvolgorde van de
 *   vragenlijst, bij elk ander vraagtype het nieuwste antwoord eerst.
 */
const orderResponsesForDisplay = (responses, matrixRows, fieldType) => {
    const rows = Array.isArray(responses) ? responses : [];
    // Het veld beslist. Zolang dat nog niet geladen is: elk antwoord mag het
    // zeggen, niet alleen het eerste — dat was inbox #190.
    const isMatrix = fieldType
        ? isMatrixType(fieldType)
        : rows.some((row) => isMatrixType(row?.FieldType));

    if (!isMatrix) {
        // Nieuwste eerst. Een antwoord zonder bruikbare tijdstempel zakt niet
        // weg maar houdt zijn plek achteraan.
        const millis = (response) => response?.Timestamp?.toDate?.()?.getTime?.() ?? -Infinity;
        return [...rows].sort((a, b) => millis(b) - millis(a));
    }

    const rowOrder = {};
    (Array.isArray(matrixRows) ? matrixRows : []).forEach((row, index) => {
        rowOrder[row.ID] = Number.isFinite(row.Position) ? row.Position : index;
    });

    // Een antwoord op een rij die intussen verwijderd is hoort nog steeds in
    // beeld — achteraan, niet weggelaten.
    return [...rows].sort((a, b) => (rowOrder[a.RowID] ?? Infinity) - (rowOrder[b.RowID] ?? Infinity));
};

export { isMatrixType };
export default orderResponsesForDisplay;
